import React, { useState, useEffect, useRef } from 'react';
import { FiMic, FiSquare, FiTrash2 } from 'react-icons/fi';
import './AudioRecorder.css';

interface AudioRecorderProps {
    onRecordingComplete: (file: File | null) => void;
    existingAudioUrl?: string | null;
    tooltip?: string;
}

const AudioRecorder: React.FC<AudioRecorderProps> = ({ 
    onRecordingComplete, 
    existingAudioUrl,
    tooltip = "Graba tus observaciones sobre lo que has presenciado en la videoconferencia. Esta grabación se transcribirá automáticamente y se incorporará al informe pericial." 
}) => {
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [audioUrl, setAudioUrl] = useState<string | null>(existingAudioUrl || null);

    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const chunksRef = useRef<Blob[]>([]);
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        setAudioUrl(existingAudioUrl || null);
    }, [existingAudioUrl]);

    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
                mediaRecorderRef.current.stop();
                mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
            }
        };
    }, []);

    const prvStartRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaRecorderRef.current = new MediaRecorder(stream);
            chunksRef.current = [];

            mediaRecorderRef.current.ondataavailable = (e: BlobEvent) => {
                if (e.data.size > 0) {
                    chunksRef.current.push(e.data);
                }
            };

            mediaRecorderRef.current.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
                const url = URL.createObjectURL(blob);
                setAudioUrl(url);

                const file = new File([blob], "grabacion.webm", { type: 'audio/webm' });
                onRecordingComplete(file);
            };

            mediaRecorderRef.current.start();
            setIsRecording(true);

            timerRef.current = setInterval(() => {
                setRecordingTime(prev => prev + 1);
            }, 1000);

        } catch (err) {
            console.error('Error accessing microphone:', err);
            alert('No se pudo acceder al micrófono. Por favor, verifica los permisos.');
        }
    };

    const prvStopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
            setIsRecording(false);
            setRecordingTime(0);
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        }
    };

    const prvFormatTime = (seconds: number): string => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const prvDeleteRecording = () => {
        setAudioUrl(null);
        onRecordingComplete(null);
    };

    return (
        <div className="audio-recorder">
            {!audioUrl ? (
                <div className="recorder-controls">
                    {!isRecording ? (
                        <button type="button" className="btn btn-record" onClick={prvStartRecording} title={tooltip}>
                            <span className="icon"><FiMic /></span> Grabar Notas
                        </button>
                    ) : (
                        <button type="button" className="btn btn-recording" onClick={prvStopRecording} title="Haz clic para detener la grabación">
                            <span className="icon"><FiSquare /></span> Grabar Notas ({prvFormatTime(recordingTime)})
                        </button>
                    )}
                </div>
            ) : (
                <div className="audio-player">
                    <audio controls src={audioUrl} className="w-full" />
                    <button type="button" className="btn-icon delete" onClick={prvDeleteRecording} title="Eliminar grabación">
                        <FiTrash2 />
                    </button>
                </div>
            )}
            {isRecording && (
                <div className="recorder-helper">
                    Grabando... Habla claro.
                </div>
            )}
        </div>
    );
};

export default AudioRecorder;
